/**
 * Standard table footer: rows-per-page + range on the left, Pagination on the right.
 * See UNIFY_UI_STYLE_GUIDE.md §4 Tables.
 */

import { Group, Pagination, Select, Text } from '@mantine/core';

/** Default options aligned with caseload and supervision tables. */
export const TABLE_ROWS_PER_PAGE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
] as const;

export interface TablePaginationFooterProps {
  totalItems: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  rowsPerPage: string;
  onRowsPerPageChange: (value: string | null) => void;
  /** Passed to Pagination for a11y */
  'aria-label'?: string;
}

export function TablePaginationFooter({
  totalItems,
  currentPage,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  'aria-label': ariaLabel = 'Table pagination',
}: TablePaginationFooterProps): JSX.Element {
  const itemsPerPage = parseInt(rowsPerPage || '50', 10);
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const start = totalItems === 0 ? 0 : (safePage - 1) * itemsPerPage + 1;
  const end = Math.min(safePage * itemsPerPage, totalItems);

  return (
    <Group justify="space-between" align="center" wrap="wrap" mt="md" gap="sm">
      <Group gap="sm" wrap="wrap">
        <Text size="sm" c="dimmed">
          Rows per page:
        </Text>
        <Select
          value={rowsPerPage}
          onChange={(value) => {
            onRowsPerPageChange(value);
            onPageChange(1);
          }}
          data={[...TABLE_ROWS_PER_PAGE_OPTIONS]}
          w={80}
          size="xs"
          aria-label="Rows per page"
        />
        <Text size="sm" c="dimmed">
          {totalItems === 0 ? '0–0 of 0' : `${start}–${end} of ${totalItems}`}
        </Text>
      </Group>
      <Pagination.Root total={totalPages} value={safePage} onChange={onPageChange} size="xs" aria-label={ariaLabel}>
        <Group gap={5}>
          <Pagination.First aria-label="First page" />
          <Pagination.Previous aria-label="Previous page" />
          <Pagination.Items />
          <Pagination.Next aria-label="Next page" />
          <Pagination.Last aria-label="Last page" />
        </Group>
      </Pagination.Root>
    </Group>
  );
}
